
import MediaHelper from '../helpers/MediaHelper';
import RenderHelper from '../helpers/RenderHelper';
import { make_link_relative } from './Util.js';

function generateRandomString( length = 30, characterSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789' ) {
	// Initialize an empty string to store the result
	let result = "";

	// Loop to generate a string of the desired length
	for ( let i = 0; i < length; i++ ) {
		// Pick a random character from the character set and append to the result
		result += characterSet.charAt( Math.floor( Math.random() * characterSet.length ) );
	}

	// Return the generated string
	return result;
}

const simpleHash = ( str ) => {
	let hash = 0
	for ( let i = 0; i < str.length; i++ ) {
		hash = ( ( hash << 5 ) - hash + str.charCodeAt( i ) ) | 0
	}
	return ( hash >>> 0 ).toString( 36 )
}

class SuggestHandler {
	isOpen = false;
	isLocked = false;

	renderedSuggestions = {};
	currentSearch = null;

	constructor( searchEl ) {
		this.searchEl = searchEl;
		this.formEl = searchEl.querySelector( 'form' );
		this.inputEl = this.formEl.querySelector( 'input.mdl_search__input' );
		this.dialogEl = searchEl.querySelector( '.mdl_search__box' );
		this.dialogInnerEl = searchEl.querySelector( '.mdl_search__box-inner' );

		// Start suggest on input and search on submit.
		this.inputEl.addEventListener( 'input', () => this.onChange() );
		this.formEl.addEventListener( 'submit', ( ev ) => this.onSubmit( ev ) );

		// Handle events related to opening or clicks.
		this.inputEl.addEventListener( 'focusin', () => this.open() );
		this.searchEl.addEventListener( 'click', ( ev ) => {
			if ( ev.target.matches( 'input[type=search]' ) ) {
				this.open();
			} else if ( ev.target.closest( '.mdl_search__close-button,.mdl_search__box-close-button' ) ) {
				this.close();
			} else if ( ev.target.closest( 'a[data-suggest]' ) ) {
				this.trackSuggestClick( ev.target.closest( 'a[data-suggest]' ) );
			}
		} );

		// Conditional Close on click or focus outside or when ESC Key Pressed.
		document.addEventListener( 'click', ( ev ) => {
			if ( ev.target.closest( '.mdl_header__user-nav-item--search .mdl_header__user-nav-item-link' ) ) {
				this.open();
			} else {
				this.maybeClose( ev )
			}
		} );
		document.addEventListener( 'focusin', ( ev ) => this.maybeClose( ev ) );
		document.addEventListener( 'keydown', ( ev ) => this.maybeClose( ev ) );

		// Lock Bodyscroll on Breakpointchange.
		MediaHelper.watch( () => this.lockScroll( this.isOpen ), 'desktop' );

		// First opening, if the inputEl has a value (only on aftersearch page).
		if ( this.inputEl.value ) {
			this.onChange();
		}
	}

	onSubmit( ev ) {

		// Default was already prevented, we do nothing too.
		if ( ev.isDefaultPrevented ) {
			return;
		}

		const fakeInput = this.formEl.querySelector( '.mdl_search__fakeinput' );
		if ( fakeInput && fakeInput.value !== '' ) {
			ev.preventDefault();
			// Return immediately, this is a bot, and we do not like bots calling our search API.
			return;
		}

		// Do not submit, while a search is in progress, wait until the search finished, then try to submit again.
		if ( this.currentSearch ) {
			ev.preventDefault();
			this.currentSearch.then( () => this.formEl.requestSubmit() );
			return;
		}

		// Special case for Mike B. - Go immediately to /shop/.
		if ( this.inputEl.value === '' ) {
			ev.preventDefault();
			this.trackSearch();
			window.location.href = '/shop/';
			return;
		}

		// Check specific Suggestions, so we can redirect, if needed.
		const searchableSuggestionEntries = {
			// If we hit a redirect or brand, follow immediately.
			'redirect': this.renderedSuggestions['redirect'] ?? [],
			'brand': this.renderedSuggestions['brand'] ?? [],

			// Categories and Products should only be considered, if there is only one result found.
			'category': ( this.renderedSuggestions['category'] ?? [] ).filter( ( category, index, array ) => array.length === 1 ),
			'productName': ( this.renderedSuggestions['productName'] ?? [] ).filter( ( productName, index, array ) => array.length === 1 )
		};

		for ( const [type, searchableSuggestions] of Object.entries( searchableSuggestionEntries ) ) {

			// If we hit any value, we follow immediately.
			const [foundSuggestion, additionalFilters] = this.findSuggestion( [type, searchableSuggestions], this.inputEl.value );
			if ( foundSuggestion ) {
				const canRedirect = this.formEl.dispatchEvent( new CustomEvent( 'redirect_searchbox.sl', { bubbles: true, cancelable: true, detail: {query: this.inputEl.value, suggestion: foundSuggestion} } ) );
				if ( canRedirect ) {
					ev.preventDefault();
					this.trackSuggest( foundSuggestion.type, foundSuggestion.searchParams );
					const targetLink = new URL( foundSuggestion.Deeplink, window.location.origin );
					if ( additionalFilters ) {
						additionalFilters.forEach( ( [ key, value ] ) => {
							targetLink.searchParams.append( key, value );
						} );
					}
					window.location.href = targetLink.toString();
					return;
				}
			}
		}

		this.trackSearch();
	}

	findSuggestion( [type, searchableSuggestions], searchString ) {
		if ( 'productName' === type ) {
			return [ searchableSuggestions.find( ( { attributes } ) => [ attributes.Artikelnummer, attributes.ProductID, attributes.Ean ].includes( searchString ) ) ];
		}

		if ( 'brand' === type ) {
			const filterList = {
				'theme': {
					'herren': [ 'Herren', 'Männer' ],
					'damen': [ 'Damen', 'Frauen' ],
					'kinder': [ 'Kinder' ]
				}
			}

			for ( const [ name, termList ] of Object.entries( filterList ) ) {
				for ( const [filter, terms] of Object.entries( termList ) ) {
					for ( const term of terms ) {
						const regex = new RegExp( '\\s*' + term + '\\s*', 'i' );
						if ( searchString.match( regex ) ) {
							const newSearchString = searchString.replace( regex, ' ' ).trim();
							const foundSuggestion = searchableSuggestions.find( ( { name } ) => this.isExactlySearchedTerm( name, newSearchString ) );
							if ( foundSuggestion ) {
								return [ foundSuggestion, [[name, filter]] ];
							}
						}
					}
				}
			}
		}

		return [searchableSuggestions.find( ( { name } ) => this.isExactlySearchedTerm( name, searchString ) )];
	}

	isExactlySearchedTerm( term, search ) {
		return search === term || ( 0 === String( term ).localeCompare( search, document.documentElement.lang, { sensitivity: 'base' } ) );
	}

	open() {
		this.isOpen = true;
		this.searchEl.classList.add( 'is-active' );
		this.dialogEl.dispatchEvent( new CustomEvent( 'open_searchbox.sl', { bubbles: true } ) );
		this.lockScroll( this.isOpen );
	}

	close() {
		this.isOpen = false;
		this.searchEl.classList.remove( 'is-active' );
		this.dialogEl.dispatchEvent( new CustomEvent( 'close_searchbox.sl', { bubbles: true } ) );
		this.lockScroll( this.isOpen );
	}

	cancel() {
		this.searchEl.classList.remove( 'has-results', 'has-noresults', 'is-loading' );
		clearTimeout( this.timeout );
		if ( this.AbortController ) {
			this.AbortController.abort();
			this.AbortController = null;
		}
	}

	maybeClose( ev ) {
		if ( this.isOpen ) {
			switch ( ev.type ) {
				case 'keydown':
					if ( ev.keyCode === 27 ) {
						this.close();
					}
					break;
				default:
					if ( ! ev.target.closest( 'search.mdl_search' ) ) {
						this.close();
					}
					break;
			}
		}
	}

	setLoading( isLoading ) {
		this.searchEl.classList.toggle( 'is-loading', isLoading );
	}

	static getSession() {
		let sid = window.localStorage.getItem( 'ff_sid' );
		if ( sid ) {
			return sid;
		}

		sid = generateRandomString( 30 );
		window.localStorage.setItem( 'ff_sid', sid );
		return sid;
	}

	lockScroll( doLock = true ) {
		const scrollLock = window.scrollLock;
		if ( doLock && MediaHelper.matches( null, 'desktop' ) ) {
			if ( ! this.isLocked ) {
				// Enable scrollLock on Dialog Element.
				scrollLock.disablePageScroll( this.dialogInnerEl );
				this.isLocked = true;
			}
		} else if ( this.isLocked ) {
			scrollLock.enablePageScroll( this.dialogInnerEl );
			this.isLocked = false;
		}
	}

	onChange() {
		if ( String( this.inputEl.value ).trim().length < 2 ) {
			this.cancel();
			return;
		}

		const query = this.inputEl.value;
		this.setLoading( true );
		this.currentSearch = this.search( query )
			.then( ( { suggestions } ) => {
				this.searchEl.classList.remove( 'has-results', 'has-noresults' );
				this.searchEl.classList.add( Object.keys( suggestions ).length === 0 ? 'has-noresults' : 'has-results' );
				return this.render( { suggestions, query } );
			} )
			.then( ( renderedHTMLFragment ) => {
				this.dialogEl.querySelector( '.mdl_search__suggestions' ).replaceChildren( renderedHTMLFragment );
			} )
			.finally( () => {
				this.setLoading( false );
				this.currentSearch = null;
			} );
	}

	render( { suggestions: suggestionList, query } ) {
		const suggestions = {};
		suggestionList.forEach( ( suggestion ) => {
			suggestions[ suggestion.type ] ??= [];
			suggestions[ suggestion.type ].push( suggestion );
		} );

		const maxSideOneEntryCount = 9;

		const sideEntryCount = Object.keys( suggestions ).reduce( ( acc, key ) => {
			return key === 'productName' ? acc : acc + suggestions[key].length
		}, 0 );

		this.renderedSuggestions = suggestions;

		const {brand, category, searchTerm, productName} = suggestions;
		let sideOneSuggests = {};
		let sideTwoSuggests = {};
		let mainSuggests = { ...( productName ? {productName} : {} ) };

		if( sideEntryCount < maxSideOneEntryCount ) {
			sideOneSuggests = {
				...( searchTerm ? {searchTerm} : {} ),
				...( category ? {category} : {} ),
				...( brand ? {brand} : {} ),
			};
		} else {
			sideOneSuggests = { ...( searchTerm ? {searchTerm} : {} ) };
			sideTwoSuggests = {
				...( category ? {category} : {} ),
				...( brand ? {brand} : {} ),
			};
		}

		let productCols = 4;
		if( Object.keys( sideOneSuggests ).length && Object.keys( sideTwoSuggests ).length  ) {
			productCols = 2;
		} else if( Object.keys( sideOneSuggests ).length || Object.keys( sideTwoSuggests ).length ) {
			productCols = 3;
		}

		return RenderHelper.render( 'factfinderSuggest', { sideOneSuggests, sideTwoSuggests, mainSuggests, query, productCols } ).then( ( renderedHTML ) => {
			return document.createRange().createContextualFragment( renderedHTML );
		} );
	}

	adjustSuggestData( suggestions ) {
		// Remove category entries which are inside DIOR and CHANEL paths
		suggestions = suggestions.filter( suggestion => {
			if ( suggestion.type === 'category' ) {
				return ['DIOR', 'CHANEL'].every( brandName => suggestion.name !== brandName && !suggestion.attributes.parentCategory.startsWith( brandName ) );
			}

			return true;
		} );

		return suggestions.map( ( suggestion, index ) => {

			// Add key to suggestion, so we can identify it for tracking.
			const hash = simpleHash( suggestion.name + '_'+ JSON.stringify( suggestion.searchParams ) );
			suggestion.dataSuggest = `suggest${index}_${hash}`;

			switch ( suggestion.type ) {
				case 'brand': {
					const searchParams = new URLSearchParams( {
						'cl': 'searchservice_ajax',
						'fnc': 'getMarkeForName',
						'marke': suggestion.name,
					} );
					suggestion.Deeplink ??= `/index.php?${searchParams.toString()}`;
					break;
				}
				case 'category': {
					const searchParams = new URLSearchParams( {
						'cl': 'searchservice_ajax',
						'fnc': 'getCategoryForName',
						'cat': suggestion.name,
						'parents': suggestion.attributes.parentCategory,
					} );
					suggestion.Deeplink ??= `/index.php?${searchParams.toString()}`;
					suggestion.ancestors = suggestion.attributes.parentCategory.split( '/' );
					break;
				}
				case 'productName': {
					suggestion.Deeplink ??= make_link_relative( suggestion.attributes.ProductURL );
					suggestion.ImageURL = ( suggestion.image && suggestion.image.replace( '768_1024_75', '192_256_75' ) ) || '//dummyimage.com/69x92/f5f5f5/';
					suggestion.brand = suggestion.attributes.Brand;
					break;
				}
				case 'redirect': {
					suggestion.Deeplink ??= suggestion.attributes.deeplink;
				}
			}

			return suggestion;
		} );
	}

	async search( query ) {
		const channel = this.formEl.dataset.channel || window.FFChannel;

		clearTimeout( this.timeout );
		return await new Promise( ( resolve ) => {
			this.timeout = setTimeout( () => resolve(), 250 );
		} ).then( () => {
			let factfinderUrl = `/fact-finder/suggest/${ channel }/`;
			let headers = {};
			if ( document.location.host.endsWith( '.local' ) && localStorage.getItem( 'ff-custom-login-data' ) ) {
				factfinderUrl = `https://kastner-oehler.fact-finder.at/fact-finder/rest/v5/suggest/${ channel }/`;
				headers = {
					// You can set the header with:
					// localStorage.setItem( 'ff-custom-login-data', 'Basic ' + btoa( loginDataString ) )
					'Authorization': localStorage.getItem( 'ff-custom-login-data' ),
				};
			}

			if ( this.AbortController ) {
				this.AbortController.abort();
			}
			this.AbortController = new AbortController();

			const params = new URLSearchParams( { query, sid: SuggestHandler.getSession() } );
			return fetch( `${ factfinderUrl }?${ params.toString() }`, {
				method: 'GET',
				signal: this.AbortController.signal,
				headers
			} )
				.catch( error => {
					if ( error.name === 'AbortError' ) {
						// Return unresolvable promise, so we do not proceed at all, if aborted.
						return new Promise( () => {} );
					} else {
						// Problem for someone else to handle.
						throw error;
					}
				} )
				.then( async( res ) => {
					const data = await res.json();
					data.suggestions = this.adjustSuggestData( data.suggestions );
					return data;
				} )
				.finally( () => this.AbortController = null );
		} );
	}

	trackSuggestClick( trackEl ) {

		if ( ! trackEl.dataset.suggest ) {
			return false;
		}
		const allSuggestions = Object.values( this.renderedSuggestions ).flat();
		const foundSuggestion = allSuggestions.find( suggestion => suggestion.dataSuggest === trackEl.dataset.suggest );
		if ( ! foundSuggestion ) {
			return false;
		}

		return this.trackSuggest( foundSuggestion.type, foundSuggestion.searchParams );
	}

	trackSuggest( type, searchParams ) {

		const dataLayer = window.dataLayer = window.dataLayer || [];

		const trackingData = {
			"event": "gtmSearchInteraction",
			"gtmSearchTerm": this.inputEl.value,
			"gtmSearchRedirect": searchParams, // result page that is redirected to
			"gtmSearchType": type // kind of redirect (see above 1-2-3-4)
		};

		dataLayer.push( trackingData );
	}

	trackSearch() {
		document.dispatchEvent( new CustomEvent( 'perform_search.sl', { bubbles: true, detail: { term: this.inputEl.value } } ) );

		const dataLayer = window.dataLayer = window.dataLayer || [];

		const trackingData = {
			"event": "gtmSearchInteraction",
			"gtmSearchTerm": this.inputEl.value,
			"gtmSearchRedirect": false,
			"gtmSearchType": "query"
		};

		dataLayer.push( trackingData );
	}

	static getInstance( searchEl ) {
		searchEl.KOE_Factfinder_SuggestHandler ??= new SuggestHandler( searchEl );
		return searchEl.KOE_Factfinder_SuggestHandler;
	}
}

const initSuggestHandler = ( scopeEl = document ) => {
	const searchEls = scopeEl.querySelectorAll( 'search.mdl_search' );
	searchEls.forEach( ( searchEl ) => SuggestHandler.getInstance( searchEl ) );
};

const getFactfinderSession = SuggestHandler.getSession;

export {
	SuggestHandler as default,
	initSuggestHandler,
	getFactfinderSession
};
